import { useEffect, useRef, useState } from 'react'
import { ArrowRepeat, ExclamationCircle, Power, Clock, Plus, Trash3 } from 'react-bootstrap-icons'
import { useData } from '../store.jsx'
import { PageHeader, Panel, Modal, Stat } from '../components/ui.jsx'
import { number } from '../lib/format.js'
import { xiaomiPlugs, setAt } from '../lib/firebase.js'

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] // index + 1 = day number (Monday = 1), as in functions/automation.js
const WEEKDAYS = [1, 2, 3, 4, 5]
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Budapest' }) // YYYY-MM-DD, like the function
const time = (ms) => new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

function daysLabel(days = []) {
  const d = [...days].sort()
  if (d.length === 7) return 'Every day'
  if (d.join() === WEEKDAYS.join()) return 'Mon–Fri'
  if (d.join() === '6,7') return 'Weekends'
  return d.map((n) => DAYS[n - 1]).join(' ')
}

function AutomationModal({ plug, rules, onClose }) {
  const [schedules, setSchedules] = useState(rules?.schedules || [])
  const [maxWatts, setMaxWatts] = useState(rules?.maxWatts ?? '')
  const [dailyKwh, setDailyKwh] = useState(rules?.dailyKwh ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const edit = (i, patch) => setSchedules((list) => list.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  const toggleDay = (i, day) => edit(i, { days: schedules[i].days.includes(day) ? schedules[i].days.filter((d) => d !== day) : [...schedules[i].days, day].sort() })

  async function save(e) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      await setAt(`integrations/xiaomi/automation/${plug.did}`, {
        schedules: schedules.filter((s) => (s.on || s.off) && s.days.length),
        maxWatts: Number(maxWatts) > 0 ? Number(maxWatts) : null,
        dailyKwh: Number(dailyKwh) > 0 ? Number(dailyKwh) : null,
      })
      onClose()
    } catch (e2) { setErr(e2.message); setBusy(false) }
  }

  return (
    <Modal title={`Automation · ${plug.name}${plug.room ? ` · ${plug.room}` : ''}`} onClose={onClose}>
      <form onSubmit={save}>
        <div className="fw-semibold mb-2">Schedule</div>
        {schedules.map((s, i) => (
          <div key={i} className="border rounded p-2 mb-2">
            <div className="d-flex flex-wrap gap-1 mb-2">
              {DAYS.map((label, d) => (
                <button key={d} type="button" className={`btn btn-sm ${s.days.includes(d + 1) ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ minWidth: 38 }} onClick={() => toggleDay(i, d + 1)}>{label}</button>
              ))}
            </div>
            <div className="d-flex align-items-end gap-2">
              <label className="small flex-fill">On<input type="time" className="form-control form-control-sm" value={s.on} onChange={(e) => edit(i, { on: e.target.value })} /></label>
              <label className="small flex-fill">Off<input type="time" className="form-control form-control-sm" value={s.off} onChange={(e) => edit(i, { off: e.target.value })} /></label>
              <button type="button" className="btn btn-sm btn-outline-danger" aria-label="Remove" onClick={() => setSchedules((list) => list.filter((_, j) => j !== i))}><Trash3 /></button>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={() => setSchedules((list) => [...list, { days: WEEKDAYS, on: '06:00', off: '08:00' }])}>
          <Plus /> Add time slot
        </button>
        <div className="small text-body-secondary mb-3">Leave On or Off empty to only switch one way. An Off earlier than On switches off the next morning.</div>

        <div className="fw-semibold mb-2">Switch off automatically</div>
        <div className="row g-2 mb-2">
          <label className="col small">Above (W)<input type="number" min="1" className="form-control form-control-sm" placeholder="e.g. 1500" value={maxWatts} onChange={(e) => setMaxWatts(e.target.value)} /></label>
          <label className="col small">After (kWh / day)<input type="number" min="0.01" step="0.01" className="form-control form-control-sm" placeholder="e.g. 5" value={dailyKwh} onChange={(e) => setDailyKwh(e.target.value)} /></label>
        </div>
        <div className="small text-body-secondary mb-3">Checked every minute. Once the daily kWh is used up, the plug stays off until midnight, also when switched on by the schedule or by hand.</div>
        {err && <div className="alert alert-danger py-2 small">{err}</div>}
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

export default function Plugs() {
  const xiaomi = useData().integrations?.xiaomi
  const [busy, setBusy] = useState(null) // 'refresh' or a plug's did
  const [err, setErr] = useState(null)
  const [code, setCode] = useState('')
  const [automating, setAutomating] = useState(null) // the plug whose automation is being edited

  async function run(key, payload) {
    setBusy(key); setErr(null)
    try { await xiaomiPlugs(payload) } catch (e) { setErr(e.message) }
    setBusy(null)
  }
  // Pick up changes made elsewhere (Xiaomi Home app, the plug's button, the toggle link): on open, when the tab
  // comes back into view and every 30 s while visible. Silent, so it does not block the buttons.
  // (Not while a sign-in waits for its email code: that would request a new code.)
  const needsCode = Boolean(xiaomi?.needsCode)
  const busyRef = useRef(busy)
  busyRef.current = busy
  useEffect(() => {
    if (needsCode) return
    const refresh = () => { if (!document.hidden && !busyRef.current) xiaomiPlugs().catch(() => {}) }
    refresh()
    const timer = setInterval(refresh, 30000)
    document.addEventListener('visibilitychange', refresh)
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', refresh) }
  }, [needsCode])

  const plugs = xiaomi?.plugs || []
  return (
    <>
      <PageHeader title="Smart plugs" subtitle="Xiaomi Home plugs, switched through the Xiaomi cloud.">
        <button className="btn btn-sm btn-primary" onClick={() => run('refresh')} disabled={!!busy}>
          <ArrowRepeat className={busy === 'refresh' ? 'spin' : ''} /> Refresh
        </button>
      </PageHeader>
      {xiaomi?.needsCode ? (
        <Panel title="Verify the Xiaomi sign-in" className="mb-3">
          <p className="small text-body-secondary">Xiaomi emailed a verification code to your Xiaomi account's address. Enter it here; this is needed only once.</p>
          <form className="d-flex gap-2" style={{ maxWidth: 360 }} onSubmit={(e) => { e.preventDefault(); run('code', { code }).then(() => setCode('')) }}>
            <input className="form-control" inputMode="numeric" autoComplete="one-time-code" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} required />
            <button className="btn btn-primary" disabled={!!busy || !code.trim()}>{busy === 'code' ? 'Checking…' : 'Verify'}</button>
          </form>
          {err && <div className="alert alert-danger py-2 small text-break mt-3 mb-0"><ExclamationCircle /> {err}</div>}
        </Panel>
      ) : (err || xiaomi?.error) && <div className="alert alert-danger py-2 small text-break"><ExclamationCircle /> {err || xiaomi.error}</div>}
      {plugs.length > 0 && <Overview plugs={plugs} xiaomi={xiaomi} />}
      <div className="row g-3">
        {plugs.map((p) => (
          <div key={p.did} className="col-12 col-xl-6">
            <PlugPanel plug={p} xiaomi={xiaomi} busy={busy}
              onSwitch={() => run(p.did, { did: p.did, on: !p.on })} onAutomate={() => setAutomating(p)} />
          </div>
        ))}
      </div>
      {!plugs.length && !busy && !xiaomi?.needsCode && (
        <Panel>
          <div className="small text-body-secondary">
            No plugs yet. Setup (one time):
            <ol className="mt-2 mb-0">
              <li>Store your Xiaomi account as function secrets: <code>firebase functions:secrets:set XIAOMI_USERNAME</code> (email, phone or Xiaomi ID) and <code>XIAOMI_PASSWORD</code>.</li>
              <li>If your Xiaomi Home server is not Europe, set <code>XIAOMI_REGION</code> (cn, us, ru, sg, i2) in <code>functions/.env</code>.</li>
              <li>Deploy functions (<code>npm run deploy:functions</code>), then press <b>Refresh</b>. If Xiaomi asks to verify the sign-in, enter the code it emails you.</li>
            </ol>
          </div>
        </Panel>
      )}
      {automating && <AutomationModal plug={automating} rules={xiaomi?.automation?.[automating.did]} onClose={() => setAutomating(null)} />}
    </>
  )
}

const usedToday = (energy) => (energy?.date === today() ? energy.kwh : 0)

function Overview({ plugs, xiaomi }) {
  const online = plugs.filter((p) => p.online)
  const on = online.filter((p) => p.on).length
  const watts = online.reduce((sum, p) => sum + (p.power || 0), 0)
  const counted = plugs.filter((p) => xiaomi?.energy?.[p.did])
  const kwh = counted.reduce((sum, p) => sum + usedToday(xiaomi.energy[p.did]), 0)
  return (
    <div className="row g-3 mb-3">
      <div className="col-6 col-lg-3"><Stat label="Switched on" value={`${on} / ${plugs.length}`} hint={`${online.length} online`} tone={on ? 'pos' : ''} /></div>
      <div className="col-6 col-lg-3"><Stat label="Load now" value={`${number(watts, 0)} W`} hint="All plugs together" /></div>
      <div className="col-6 col-lg-3"><Stat label="Used today" value={counted.length ? `${number(kwh, 2)} kWh` : '–'} hint={counted.length ? `${counted.length} plug${counted.length > 1 ? 's' : ''} with rules` : 'Counted once a plug has rules'} /></div>
      <div className="col-6 col-lg-3"><Stat label="Checked" value={xiaomi?.syncedAt ? time(xiaomi.syncedAt) : '–'} hint="Refreshes every 30 s" /></div>
    </div>
  )
}

/** A thin bar for a value against its limit; red from 90 %. */
function Meter({ value, max }) {
  const pct = Math.min(100, (value / max) * 100)
  return <div className="meter mt-2"><div className={`meter-fill ${pct >= 90 ? 'hot' : ''}`} style={{ width: `${pct}%` }} /></div>
}

function PlugPanel({ plug: p, xiaomi, busy, onSwitch, onAutomate }) {
  const rules = xiaomi?.automation?.[p.did]
  const energy = xiaomi?.energy?.[p.did]
  const last = xiaomi?.lastAuto?.[p.did]
  const schedules = (rules?.schedules || []).filter((s) => s.on || s.off)
  const state = !p.online ? 'offline' : p.on ? 'on' : 'off'
  const used = usedToday(energy)
  return (
    <Panel className="h-100"
      title={<span className="d-flex align-items-center gap-2 text-truncate">
        <span className={`status-dot ${state}`} />{p.name}<span className="text-body-secondary fw-normal small">{p.room}</span>
      </span>}
      action={<div className="d-flex gap-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={onAutomate}><Clock /> Automation</button>
        <button className={`btn btn-sm ${p.on ? 'btn-success' : 'btn-outline-success'}`} style={{ minWidth: 72 }} onClick={onSwitch}
          disabled={!!busy || !p.online} aria-label={p.on ? `Turn off ${p.name}` : `Turn on ${p.name}`}>
          {busy === p.did ? <span className="spinner-border spinner-border-sm" /> : <><Power /> {p.on ? 'On' : 'Off'}</>}
        </button>
      </div>}>
      <div className="row g-3 mb-3">
        <div className="col-12 col-sm-4"><Stat label="State" value={!p.online ? 'Offline' : p.on === null ? '?' : p.on ? 'On' : 'Off'}
          tone={!p.online ? 'neg' : p.on ? 'pos' : ''} hint={p.model} /></div>
        <div className="col-6 col-sm-4"><Stat label="Power" value={p.online && p.power != null ? `${number(p.power, 0)} W` : '–'}
          hint={rules?.maxWatts ? <>Limit {rules.maxWatts} W<Meter value={p.power || 0} max={rules.maxWatts} /></> : 'No limit'} /></div>
        <div className="col-6 col-sm-4"><Stat label="Today" value={energy ? `${number(used, 2)} kWh` : '–'}
          hint={rules?.dailyKwh ? <>Limit {rules.dailyKwh} kWh<Meter value={used} max={rules.dailyKwh} /></> : energy ? 'No limit' : 'Not counted'} /></div>
      </div>
      {schedules.length > 0 ? (
        <div className="table-wrap"><table className="table table-sm">
          <thead><tr><th>Schedule</th><th className="text-end">On</th><th className="text-end">Off</th></tr></thead>
          <tbody>
            {schedules.map((s, i) => (
              <tr key={i}><td>{daysLabel(s.days)}</td><td className="num">{s.on || '–'}</td>
                <td className="num">{s.off || '–'}{s.on && s.off && s.off < s.on && <span className="text-body-secondary small"> +1</span>}</td></tr>
            ))}
          </tbody>
        </table></div>
      ) : (
        <div className="small text-body-secondary">No schedule yet. Set on / off times and limits under <b>Automation</b>.</div>
      )}
      {last && (
        <div className={`small mt-2 ${last.error ? 'text-danger' : 'text-body-secondary'}`}>
          <Clock /> {time(last.at)} · {last.reason}{last.error ? ` (failed: ${last.error})` : ''}
        </div>
      )}
    </Panel>
  )
}
